import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { icons as LucideIcons } from 'lucide-react';
import { ICON_LIBRARY, IconName } from '@/types/label';
import { Search } from 'lucide-react';

interface IconPickerProps {
  value: string;
  onSelect: (iconName: string) => void;
  trigger?: React.ReactNode;
}

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onSelect,
  trigger,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filteredIcons = ICON_LIBRARY.filter((name) =>
    name.toLowerCase().includes(search.toLowerCase())
  );

  const getIcon = (name: string) => {
    const pascalCase = name
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');
    return LucideIcons[pascalCase as keyof typeof LucideIcons];
  };

  const SelectedIcon = value ? getIcon(value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            {SelectedIcon && <SelectedIcon className="w-4 h-4" />}
            {value || 'Select Icon'}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8"
            />
          </div>
        </div>
        <ScrollArea className="h-64">
          <div className="grid grid-cols-6 gap-1 p-2">
            {filteredIcons.map((name) => {
              const Icon = getIcon(name);
              if (!Icon) return null;
              
              return (
                <Button
                  key={name}
                  variant={value === name ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => {
                    onSelect(name);
                    setOpen(false);
                  }}
                  title={name}
                >
                  <Icon className="w-4 h-4" />
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
